/**
 * Where a post (or a hat's post) sits on the board, as people say it: "Div. 2 / Dept. 4
 * Promotion & Marketing" (ISS-020 hats with the same name, ISS-021 the staff card and post).
 *
 * Read from the org-doc cache (`orgParts`, `orgHats`). A department carries its number in its
 * NAME ("Department 19", the real name in subTitle) or in its SUBTITLE ("Department 4" under
 * "Promotion & Marketing") — both shapes are on the Flash board.
 */

interface PartLike {
    partKey?: string;
    key?: string;
    partType?: string;
    name?: string;
    subTitle?: string;
    parentPartKey?: string;
}

type AnyRecord = Record<string, any>;

const DIVISION = /^division\s+(\d+)$/i;
const DEPARTMENT = /^department\s+(\d+)$/i;

/** The short label for a container part: "Div. 7", "Dept. 19 Office of the CEO", "Council". */
export function containerLabel(part: PartLike): string {
    const name = (part.name || '').trim();
    const sub = (part.subTitle || '').trim();
    const type = (part.partType || '').toLowerCase();
    if (type === 'division') {
        const n = DIVISION.exec(name) || DIVISION.exec(sub);
        return n ? `Div. ${n[1]}` : name;
    }
    if (type === 'department') {
        const fromName = DEPARTMENT.exec(name);
        if (fromName) return sub ? `Dept. ${fromName[1]} ${sub}` : `Dept. ${fromName[1]}`;
        const fromSub = DEPARTMENT.exec(sub);
        return fromSub ? `Dept. ${fromSub[1]} ${name}` : name;
    }
    return name;
}

const keyOf = (p: PartLike) => p.partKey || p.key || '';

/**
 * Division / department (and section or unit, when the post sits in one) above a part. A post
 * right under an executive reads as that executive's name.
 */
export function partLocationLabel(parts: PartLike[] | undefined, partKey: string | undefined): string {
    if (!parts?.length || !partKey) return '';
    const byKey = new Map(parts.map((p) => [keyOf(p), p]));
    const start = byKey.get(partKey);
    if (!start) return '';

    const chain: PartLike[] = [];
    const seen = new Set<string>([partKey]);
    let parent = start.parentPartKey ? byKey.get(start.parentPartKey) : undefined;
    while (parent && !seen.has(keyOf(parent))) {
        seen.add(keyOf(parent));
        chain.unshift(parent);
        parent = parent.parentPartKey ? byKey.get(parent.parentPartKey) : undefined;
    }

    const typeOf = (p: PartLike) => (p.partType || '').toLowerCase();
    const division = chain.find((p) => typeOf(p) === 'division');
    const department = chain.find((p) => typeOf(p) === 'department');
    const inner = [...chain].reverse().find((p) => typeOf(p) === 'section' || typeOf(p) === 'unit');
    const labels = [division, department, inner].filter((p): p is PartLike => !!p).map(containerLabel);
    if (labels.length) return labels.filter(Boolean).join(' / ');

    const executive = [...chain].reverse().find((p) => typeOf(p) === 'executive');
    return executive ? containerLabel(executive) : '';
}

/** The post a hat sits on: the first of `positionArray`, else `orgPosition`. */
export function hatPostKey(hat: AnyRecord | undefined): string {
    if (!hat) return '';
    const positions = hat['positionArray'];
    if (Array.isArray(positions) && positions.length) {
        const first = positions[0];
        return typeof first === 'string' ? first : first?.partKey || first?.key || '';
    }
    const pos = hat['orgPosition'];
    return (pos && (pos.partKey || pos.key)) || '';
}

/** Where a hat sits, from the org doc ("" when it is on no post). */
export function hatLocationLabel(org: AnyRecord | undefined, hatKey: string): string {
    const hat = org?.['orgHats']?.[hatKey];
    return partLocationLabel(org?.['orgParts'], hatPostKey(hat));
}

/** "Admin – Div. 2 / Dept. 4 Promotion & Marketing", or just the name for a hat on no post. */
export function hatNameWithLocation(org: AnyRecord | undefined, hatKey: string): string {
    const name = org?.['orgHats']?.[hatKey]?.name || hatKey;
    const where = hatLocationLabel(org, hatKey);
    return where ? `${name} – ${where}` : name;
}

/** One post a person holds, and the offices (linked posts) that represent it. */
export interface StaffPosition {
    name: string;
    /** Where it sits ("Div. 2 / Dept. 4 Promotion & Marketing"); "" when it cannot be placed. */
    where: string;
    also: { name: string; where: string }[];
}

/**
 * The posts the staff card shows (ISS-021): each post the person holds through a hat, with
 * where it sits, and under it any office a linked post makes of it (Dept 19's "Office of the
 * CFO" for the CFO). Hats on no post are left out.
 */
export function staffPositions(org: AnyRecord | undefined, uid: string): StaffPosition[] {
    const parts: Array<PartLike & { linkedPartKey?: string }> = org?.['orgParts'] || [];
    const orgHats = org?.['orgHats'] || {};
    const byKey = new Map(parts.map((p) => [keyOf(p), p]));
    const postKeys: string[] = [];
    for (const hatKey of (orgHats['hatsKeys'] as string[]) || []) {
        const hat = orgHats[hatKey];
        if (!hat || !((hat['assignedToArray'] as string[]) || []).includes(uid)) continue;
        const postKey = hatPostKey(hat);
        if (postKey && byKey.has(postKey) && !postKeys.includes(postKey)) postKeys.push(postKey);
    }
    return postKeys.map((postKey) => ({
        name: byKey.get(postKey)?.name || '',
        where: partLocationLabel(parts, postKey),
        also: parts
            .filter((p) => p.linkedPartKey === postKey)
            .map((office) => ({ name: office.name || '', where: partLocationLabel(parts, keyOf(office)) })),
    }));
}

/** The same posts as text lines ("Admin – Div. 2 / …", "Also Office of the CFO – …"), for search. */
export function staffPositionLines(org: AnyRecord | undefined, uid: string): string[] {
    const line = (name: string, where: string) => (where ? `${name} – ${where}` : name);
    return staffPositions(org, uid).flatMap((p) => [
        line(p.name, p.where),
        ...p.also.map((o) => `Also ${line(o.name, o.where)}`),
    ]);
}
